import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, X, Sparkles, MapPin, Info, ArrowLeft, Star } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Discover() {
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [swiping, setSwiping] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/discover`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfiles(response.data);
    } catch (error) {
      toast.error('Erreur de chargement des profils');
    } finally {
      setLoading(false);
    }
  };

  const handleSwipe = async (action) => {
    if (swiping || currentIndex >= profiles.length) return;

    setSwiping(true);
    const currentProfile = profiles[currentIndex];

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('swiped_id', currentProfile.user.id);
      formData.append('action', action);

      const response = await axios.post(`${API}/swipe`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.is_match) {
        toast.success("💖 C'est un match ! Vous pouvez maintenant discuter");
      }

      setCurrentIndex(currentIndex + 1);
    } catch (error) {
      toast.error('Erreur lors du swipe');
    } finally {
      setSwiping(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-pink-500 mx-auto mb-4" />
          <p className="text-gray-600">Recherche de profils compatibles...</p>
        </div>
      </div>
    );
  }

  if (profiles.length === 0 || currentIndex >= profiles.length) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="glass-card p-12 text-center max-w-md">
          <Sparkles className="w-20 h-20 mx-auto mb-6 text-pink-500" />
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Plus de profils pour le moment</h2>
          <p className="text-gray-600 mb-8">Revenez plus tard pour découvrir de nouveaux profils !</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="btn-primary w-full py-4"
          >
            Retour au tableau de bord
          </button>
        </div>
      </div>
    );
  }

  const currentProfile = profiles[currentIndex];
  const currentUser = currentProfile.user;

  return (
    <div className="min-h-screen py-8 px-6">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/dashboard')}
            className="p-2 hover:bg-white/50 rounded-full"
            style={{ transition: 'background 0.2s' }}
          >
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
          <h1 className="text-2xl font-bold gradient-text">Découvrir</h1>
          <div className="w-10" />
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between mb-2">
            <span className="text-sm text-gray-600">{currentIndex + 1} / {profiles.length}</span>
            <span className="text-sm text-pink-600">Score: {Math.round(currentProfile.affinity_score)}/100</span>
          </div>
          <div className="h-2 bg-gray-200 rounded-full">
            <div
              className="h-full bg-gradient-to-r from-pink-500 to-orange-400 rounded-full"
              style={{ width: `${((currentIndex + 1) / profiles.length) * 100}%`, transition: 'width 0.3s' }}
            />
          </div>
        </div>

        {/* Card */}
        <div
          ref={cardRef}
          className="relative glass-card overflow-hidden mb-6"
          style={{ height: '600px' }}
        >
          {/* Photo */}
          <div className="absolute inset-0">
            {currentUser.photos && currentUser.photos.length > 0 ? (
              <img
                src={`${API}/profile/photos/${currentUser.photos[0]}`}
                alt={currentUser.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-pink-300 to-orange-300 flex items-center justify-center">
                <Heart className="w-32 h-32 text-white opacity-50" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
          </div>

          {/* Info */}
          <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
            <div className="flex items-center gap-2 mb-2">
              <h2 className="text-3xl font-bold">{currentUser.name}</h2>
              <span className="text-2xl">{currentUser.age}</span>
            </div>
            
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="w-4 h-4" />
              <span>{currentUser.location}</span>
            </div>

            {/* Social Score */}
            {currentUser.social_score && currentUser.social_score.total_ratings > 0 && (
              <div className="flex items-center gap-2 mb-3 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 inline-flex">
                <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                <span className="font-semibold">{currentUser.social_score.average}</span>
                <span className="text-xs opacity-80">({currentUser.social_score.total_ratings} avis)</span>
              </div>
            )}

            {currentUser.bio && (
              <p className="text-sm mb-3 line-clamp-2">{currentUser.bio}</p>
            )}

            {currentUser.interests && currentUser.interests.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {currentUser.interests.slice(0, 5).map(interest => (
                  <span
                    key={interest}
                    className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs"
                  >
                    {interest}
                  </span>
                ))}
              </div>
            )}

            <div className="glass-card p-3 mt-4">
              <div className="flex items-start gap-2">
                <Info className="w-5 h-5 text-pink-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-white/90">{currentProfile.match_reason}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-center gap-6">
          <button
            data-testid="dislike-btn"
            onClick={() => handleSwipe('dislike')}
            disabled={swiping}
            className="w-16 h-16 rounded-full bg-white border-4 border-red-500 flex items-center justify-center hover:bg-red-50 disabled:opacity-50"
            style={{ transition: 'all 0.2s', boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)' }}
          >
            <X className="w-8 h-8 text-red-500" />
          </button>

          <button
            data-testid="like-btn"
            onClick={() => handleSwipe('like')}
            disabled={swiping}
            className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-500 to-orange-400 flex items-center justify-center hover:scale-110 disabled:opacity-50"
            style={{ transition: 'all 0.2s', boxShadow: '0 8px 24px rgba(255, 107, 157, 0.4)' }}
          >
            <Heart className="w-10 h-10 text-white fill-white" />
          </button>
        </div>
      </div>
    </div>
  );
}
